import { useMutation } from '@tanstack/react-query';
import type { z } from 'zod';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { generateAiPetitionDraft, inferDecisionMakers } from '#src/app/pages/startAPetitionV2/shared/api';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';

import type { schema } from '../../schema';
import { useInferScopeLocation } from '../useInferScopeLocation';

import { resolveDmiSkipReason } from './utils';

type FormData = z.infer<typeof schema>;

export function useGenerateAiDraft() {
	const { data, actions } = useStartAPetitionPageState();
	const { data: pageData } = useStartAPetitionPageData();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const inferScopeLocation = useInferScopeLocation();
	const supportedDmiCountries = pageData.featureConfigs.sapDmiSupportedTargetCountries;

	const { mutateAsync: createJobAsync } = useImageJob();

	const { mutate, error, ...mutationState } = useMutation({
		// The DMI fire-and-forget branch below adds a couple of legitimate guard clauses; splitting
		// it into a separate function reads worse, not better.
		// oxlint-disable-next-line complexity
		mutationFn: async (formData: FormData) => {
			// TODO: remove non-null assertion with schema validation _just incase_
			const input = { mainPrompt: data.draft?.input?.issuePrompt ?? '', personalStory: formData.personalStory };

			// Scope/location inference (CHANGE-97331 + CHANGE-97720) is settled alongside the draft/image so
			// its result lands on the draft deterministically at this submit, rather than racing a later step
			// via a self-submit. It can reject; `allSettled` means that never fails this submit. Durable
			// once-per-draft: a prior successful result is reused instead of re-fired (survives the remount
			// when the starter goes back to revise their story); a prior failure leaves this null and retries.
			const existingInference = data.draft?.metadata?.inferredScopeLocation ?? null;

			const [aiDraftResponse, imageResponse, inferenceResponse] = await Promise.allSettled([
				generateAiPetitionDraft(input, utilityContext, track),
				createJobAsync(input),
				existingInference ? Promise.resolve(existingInference) : inferScopeLocation(input),
			]);

			if (aiDraftResponse.status === 'rejected') throw aiDraftResponse.reason;

			const image =
				imageResponse.status === 'fulfilled' ? { image: { responseId: imageResponse.value.jobId } } : undefined;

			const inferredScopeLocation =
				inferenceResponse.status === 'fulfilled' ? inferenceResponse.value : existingInference;

			// DMI (CHANGE-98274): fire-and-forget -- its own timeout (55s) must never hold up this submit.
			// Errors are already tracked inside inferDecisionMakers's own error handling. `sap_dmi_eligibility`
			// gives a denominator for "of eligible drafts, how many completed", since every skip path below
			// is otherwise invisible.
			const dmiSkipReason = resolveDmiSkipReason(existingInference, inferredScopeLocation, supportedDmiCountries);
			void track(
				'sap_dmi_eligibility',
				dmiSkipReason ? { dmi_fired: false, dmi_skip_reason: dmiSkipReason } : { dmi_fired: true },
			);
			if (!dmiSkipReason && inferredScopeLocation) {
				// Retain the inferred recommendations on the draft (CHANGE-99392) so the later DM UI +
				// attach-at-publish can reuse the exact set we got: the petition doesn't exist yet (preview),
				// and DMI is non-deterministic, so we can't re-infer at publish time. Still fire-and-forget --
				// persisted via a self-submit only once the promise resolves, never awaited in the submit path,
				// so DMI's 55s budget can't hold up this submit. Durable metadata patch (same shape as
				// useImageJob's imageJobId self-submit); errors are already tracked inside inferDecisionMakers,
				// so a rejection or a null result simply leaves the slot unset.
				void (async () => {
					try {
						const dmiResult = await inferDecisionMakers(
							{
								title: input.mainPrompt,
								location: {
									city: inferredScopeLocation.city,
									adminArea: inferredScopeLocation.adminArea,
									countryCode: inferredScopeLocation.countryCode,
								},
								draftId: data.draft?.id,
							},
							utilityContext,
							track,
						);
						if (!dmiResult) return;
						actions.send({
							type: 'submit',
							target: 'self',
							draft: {
								metadata: {
									inferredDecisionMakers: dmiResult.recommendations.map((rec) => ({
										canonicalPetitionTargetId: rec.canonicalPetitionTargetId,
										politicianId: rec.politicianId,
										name: rec.name,
										position: rec.position,
									})),
								},
							},
						});
					} catch {
						// best-effort: never surface a DMI failure to the submit flow.
					}
				})();
			}

			return { ...aiDraftResponse.value, ...image, inferredScopeLocation };
		},
		onMutate: (formData) => {
			void track('sap_step_submit', {
				step: 'personal_story',
				step_data: { personal_story: formData.personalStory },
				step_data_raw: JSON.stringify({ personal_story: formData.personalStory }),
			});
		},
		onSuccess: (response) => {
			const recommendedDraft = { ask: response.ask, description: response.description, image: null };
			const metadata = {
				imageJobId: response.image?.responseId ?? null,
				inferredScopeLocation: response.inferredScopeLocation,
			};
			actions.send({
				type: 'submit',
				draft: { recommendedDraft, petitionDraft: recommendedDraft, editingDraft: null, metadata },
			});
		},
	});

	return { mutate, error, mutationState };
}
